import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { ErrorBoundary } from "react-error-boundary";
import analyticsUsageApi from "../../api/analytics/analyticsUsageApi";
import AnalyticsPage, {
  AnalyticsPageLoadContext,
} from "../../components/analytics/AnalyticsPage";
import { HorizontalBarChart } from "../../components/analytics/charts/BarCharts";
import ChartError from "../../components/analytics/charts/ChartError";
import { ChartDrilldownParams } from "../../types/analytics/ChartDrilldownParams";
import { BarChartDrilldownValue } from "../../types/analytics/charts/BarChartDrilldownValue";
import UsageDataTabPanel from "../../components/analytics/usage/UsageDataTabPanel";
import UsageJourneyCompletionCards from "../../components/analytics/usage/UsageJourneyCompletionCards";
import { UsageStatusSegmentClick } from "../../components/analytics/usage/UsageStatusDistributionBar";
import UsageStatusOverview from "../../components/analytics/usage/UsageStatusOverview";
import { UsageStatusDataset } from "../../types/analytics/UsageStatusDataset";
import {
  UsageAccountabilityExplorerDto,
  UsageJourneyStatusPageDto,
  AnalyticsPageDataDto,
} from "../../types/analytics/AnalyticsPageDataDto";
import { UsageExplorerViewToken } from "../../types/analytics/UsageExplorerViewToken";
import UsageAccountabilityExplorer from "../../components/analytics/usage/UsageAccountabilityExplorer";
import UsageFeatureSections from "../../components/analytics/usage/UsageFeatureSections";
import AnalyticsFilterInputs from "../../types/analytics/AnalyticsFilterInputs";
import { ScrollMainContainerToElement } from "../../components/layout/MainContainer";
import { AnalyticsWidgetUiDetailsDto } from "../../types/analytics";
import { GroupedJourneys } from "../../types/analytics/FilterAvailableValues";
import { KeyValuePair } from "../../types/generic";
import Tabs, { TabDetails } from "../../components/common/Tabs";
import UserContext from "../../state/UserContext";
import { getUsageSectionTabClassNames } from "../../helpers/usage/usageTabClassNames";
import { AnalyticsLoadingSpinner } from "../../components/analytics/AnalyticsLoadingSpinner";
import { AnalyticsEmptyFilteredState } from "../../components/analytics/AnalyticsEmptyFilteredState";
import {
  createExplorerViewToken,
} from "../../helpers/usage/usageExplorerTokens";
import {
  UsageTableFilterChipLink,
} from "../../helpers/usage/usageTableFilterHelpers";

export type UsagePageView = "overview" | "focus";

function UsageReport() {
  const { t } = useTranslation();
  const auth = useAuth();
  const userContext = useContext(UserContext);
  const usageApi = useMemo(
    () => new analyticsUsageApi(auth.user?.access_token),
    [auth.user?.access_token],
  );
  const clientColour = userContext.user.client.primaryHexColour;
  const canSendNudges = userContext.user.isAdmin;
  const [journeyStatus, setJourneyStatus] =
    useState<UsageJourneyStatusPageDto | null>(null);
  const [explorerView, setExplorerView] =
    useState<UsageExplorerViewToken | null>(null);
  const [appliedFilters, setAppliedFilters] =
    useState<AnalyticsFilterInputs | null>(null);
  const [widgets, setWidgets] = useState<AnalyticsWidgetUiDetailsDto[]>([]);
  const [journeys, setJourneys] = useState<GroupedJourneys[]>([]);
  const [pageView, setPageView] = useState<UsagePageView>("overview");
  const [tableDrilldownParams, setTableDrilldownParams] = useState<
    KeyValuePair<string, string>[] | null
  >(null);
  // The table remounts on these params. Debounce so rapid card/bar clicks collapse
  // into one fetch; the chip and selected card still follow tableDrilldownParams.
  const [appliedTableDrilldownParams, setAppliedTableDrilldownParams] =
    useState<KeyValuePair<string, string>[] | null>(null);
  const [tableFilterLabel, setTableFilterLabel] = useState<string | null>(null);
  const [tableFilterStatusKey, setTableFilterStatusKey] = useState<
    string | null
  >(null);
  const [explorerData, setExplorerData] =
    useState<UsageAccountabilityExplorerDto | null>(null);
  const [explorerLoading, setExplorerLoading] = useState(false);
  const [explorerError, setExplorerError] = useState(false);
  const [scrollToExplorerPending, setScrollToExplorerPending] = useState(false);
  const explorerRequestGenerationRef = useRef(0);

  const usageWidget = useMemo(
    () =>
      widgets.find((widget) => widget.widgetType === "USAGE-SUMMARY") ?? null,
    [widgets],
  );

  const overviewTabTitle = t(
    "Analytics.Widgets.Usage.PageTabs.Overview",
    "Overview",
  );
  const focusTabTitle = t(
    "Analytics.Widgets.Usage.PageTabs.WhereToFocus",
    "Where to focus",
  );

  const loadExplorerData = (filters: AnalyticsFilterInputs) => {
    const requestGeneration = ++explorerRequestGenerationRef.current;
    setExplorerLoading(true);
    setExplorerError(false);
    usageApi.getExplorerData(
      filters,
      (data) => {
        if (requestGeneration !== explorerRequestGenerationRef.current) return;
        setExplorerData(data);
        setExplorerLoading(false);
      },
      () => {
        if (requestGeneration !== explorerRequestGenerationRef.current) return;
        setExplorerData(null);
        setExplorerLoading(false);
        setExplorerError(true);
      },
    );
  };

  useEffect(() => {
    if (pageView !== "focus" || !appliedFilters || explorerData || explorerLoading) {
      return;
    }

    loadExplorerData(appliedFilters);
    // Intentionally only react to tab/filter/cache state — loadExplorerData closes over usageApi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageView, appliedFilters, explorerData, explorerLoading]);

  useEffect(() => {
    if (
      pageView !== "focus" ||
      !scrollToExplorerPending ||
      !explorerData ||
      explorerLoading
    ) {
      return;
    }

    requestAnimationFrame(() => {
      ScrollMainContainerToElement(
        document.getElementById("usage-explorer-heading"),
      );
      setScrollToExplorerPending(false);
    });
  }, [pageView, scrollToExplorerPending, explorerData, explorerLoading]);

  useEffect(() => {
    if (tableDrilldownParams === null) {
      setAppliedTableDrilldownParams(null);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setAppliedTableDrilldownParams(tableDrilldownParams);
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [tableDrilldownParams]);

  const goToFocusTab = (options?: {
    explorerToken?: UsageExplorerViewToken | null;
    scrollToExplorer?: boolean;
  }) => {
    if (options?.explorerToken !== undefined) {
      setExplorerView(options.explorerToken);
    }
    if (options?.scrollToExplorer) {
      setScrollToExplorerPending(true);
    }
    setPageView("focus");
  };

  const handleChartDrilldown = (params: ChartDrilldownParams) => {
    setTableDrilldownParams([
      { key: "drilldownValue", value: params.drilldownValue },
      { key: "drilldownType", value: params.drilldownType },
      {
        key: "outerDrilldownValue",
        value: params.outerDrilldownValue ?? "",
      },
    ]);
    setTableFilterLabel(params.drilldownValue);
    setTableFilterStatusKey(params.statusKey ?? null);
    setPageView("overview");
  };

  const handleClearTableFilter = () => {
    setTableDrilldownParams(null);
    setTableFilterLabel(null);
    setTableFilterStatusKey(null);
  };

  const handleNavigateToFocusFromChip = (link: UsageTableFilterChipLink) => {
    if (link === "managers") {
      goToFocusTab({
        explorerToken: createExplorerViewToken("WAITING_ON_MANAGER_STALLED"),
        scrollToExplorer: true,
      });
      return;
    }

    goToFocusTab();
  };

  const handleGoToManagersFromBar = () => {
    goToFocusTab();
  };

  const handlePageDataChanged = (
    data: AnalyticsPageDataDto | null,
    filters: AnalyticsFilterInputs,
    context?: AnalyticsPageLoadContext,
  ) => {
    explorerRequestGenerationRef.current += 1;
    setJourneyStatus(data?.journeyStatus ?? null);
    setExplorerView(null);
    setAppliedFilters(filters);
    setWidgets(context?.widgets ?? []);
    setJourneys(context?.journeys ?? []);
    setPageView("overview");
    setTableDrilldownParams(null);
    setTableFilterLabel(null);
    setTableFilterStatusKey(null);
    setExplorerData(null);
    setExplorerError(false);
    setExplorerLoading(false);
    setScrollToExplorerPending(false);
  };

  const handlePageTabChange = (tabName: string) => {
    if (tabName === overviewTabTitle) {
      setPageView("overview");
      return;
    }

    if (tabName === focusTabTitle) {
      setPageView("focus");
    }
  };

  const renderFocusTabContent = () => {
    if (!usageWidget || !appliedFilters) {
      return null;
    }

    if (explorerLoading && !explorerData) {
      return (
        <div className="mx-3 mt-8">
          <AnalyticsLoadingSpinner />
        </div>
      );
    }

    if (explorerError && !explorerData) {
      return (
        <div className="mx-3 mt-8 rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
          <p>
            {t(
              "Analytics.Widgets.Usage.Explorer.LoadError",
              "Unable to load the accountability breakdown. Please try again.",
            )}
          </p>
          <button
            type="button"
            className="btn-secondary btn-small mt-3"
            onClick={() => loadExplorerData(appliedFilters)}
          >
            {t("Common.TryAgain", "Try again")}
          </button>
        </div>
      );
    }

    if (!explorerData) {
      return null;
    }

    return (
      <>
        <UsageAccountabilityExplorer
          key={JSON.stringify(appliedFilters)}
          explorer={explorerData}
          viewToken={explorerView}
          onViewTokenChange={setExplorerView}
          widget={usageWidget}
          filters={appliedFilters}
          canSendNudges={canSendNudges}
        />
        <UsageFeatureSections
          filters={appliedFilters}
          widget={usageWidget}
          explorerViewToken={explorerView}
          onExplorerViewChange={setExplorerView}
        />
        <div id="usage-pace-activity-slot" className="mx-3 mt-8" />
      </>
    );
  };

  const renderPageContent = () => {
    if (!appliedFilters) {
      return null;
    }

    // The page suppresses the default widget cards, so it has to render the
    // shared "no data for these filters" state itself.
    if (!journeyStatus || !usageWidget) {
      return <AnalyticsEmptyFilteredState />;
    }

    const filters = appliedFilters;

    const tabs: TabDetails[] = [
      {
        type: "overview",
        title: overviewTabTitle,
        content: (
          <>
            <UsageOverviewSummary
              widget={usageWidget}
              journeyStatus={journeyStatus}
              onChartDrilldown={handleChartDrilldown}
              onGoToManagers={handleGoToManagersFromBar}
              selectedStatusKey={tableFilterStatusKey}
            />
            <UsageDataTabPanel
              key={`data-${JSON.stringify(filters)}-${JSON.stringify(appliedTableDrilldownParams)}`}
              widget={usageWidget}
              filters={filters}
              journeys={journeys}
              otherApiParams={appliedTableDrilldownParams}
              filterLabel={tableFilterLabel}
              filterStatusKey={tableFilterStatusKey}
              onClearFilter={handleClearTableFilter}
              onNavigateToFocus={handleNavigateToFocusFromChip}
              canSendNudges={canSendNudges}
            />
          </>
        ),
      },
      {
        type: "focus",
        title: focusTabTitle,
        content: renderFocusTabContent(),
      },
    ];

    return (
      // !pt-0 overrides #analytics-widgets-container > div { py-4 } so tabs sit under the filter band.
      <div className="mx-3 !pt-0">
        <Tabs
          tabs={tabs}
          selectedTabClassNames={getUsageSectionTabClassNames(clientColour)}
          contextClassNames="!py-0"
          tabListWidth="w-1/3"
          maxTabsOnSmallScreens={3}
          initialTabToOpenToUsingTabType={pageView}
          onActiveTabChangeEvent={handlePageTabChange}
        />
      </div>
    );
  };

  return (
    <AnalyticsPage
      pageIdentifier="UsageReport"
      title="Pages.Analytics.Usage.PageTitle"
      journeyFilterDefaultValueMode="SINGLE"
      contentType="WIDGETS"
      journeyFilterRestriction="SHOW-ALL"
      analyticsPageApi={usageApi}
      suppressDefaultWidgets
      onPageDataChanged={handlePageDataChanged}
    >
      {renderPageContent()}
    </AnalyticsPage>
  );
}

function UsageOverviewSummary({
  widget,
  journeyStatus,
  onChartDrilldown,
  onGoToManagers,
  selectedStatusKey,
}: {
  widget: AnalyticsWidgetUiDetailsDto;
  journeyStatus: UsageJourneyStatusPageDto;
  onChartDrilldown: (params: ChartDrilldownParams) => void;
  onGoToManagers: () => void;
  selectedStatusKey?: string | null;
}) {
  const { t } = useTranslation();
  const datasets = (widget.datasets ?? []) as UsageStatusDataset[];
  const isMultiJourney = datasets.length > 1;

  const handleDrilldown = (
    drilldownValue: string,
    searchType: "EXACT" | "RANGE" = "EXACT",
    outerDrilldownValue?: string,
    statusKey?: string,
  ) => {
    onChartDrilldown({
      drilldownValue,
      drilldownType: searchType,
      outerDrilldownValue,
      statusKey,
    });
  };

  const handleSegmentFilter = (segment: UsageStatusSegmentClick) => {
    handleDrilldown(segment.displayLabel, "EXACT", undefined, segment.id);
  };

  const handleBarChartClick = (item: BarChartDrilldownValue) => {
    handleDrilldown(
      item.value.toString(),
      "EXACT",
      item.stackedOuterValue?.toString(),
    );
  };

  return (
    <section
      className="mx-3 mt-4"
      aria-label={t("Analytics.Widgets.Usage.UsageSummary.Heading", "Summary")}
    >
      {isMultiJourney ? (
        <>
          <UsageStatusOverview widget={widget} variant="default" />

          <UsageJourneyCompletionCards datasets={datasets} />

          <div className="mt-4">
            <ErrorBoundary FallbackComponent={ChartError}>
              <HorizontalBarChart
                widget={widget}
                isExportingImage={false}
                onBarClick={handleBarChartClick}
              />
            </ErrorBoundary>
          </div>
        </>
      ) : (
        <UsageStatusOverview
          widget={widget}
          variant="hero"
          waitingOnManagerStalledCount={
            journeyStatus.waitingOnManagerStalledCount
          }
          agingCardsUnlocked={journeyStatus.agingCardsUnlocked}
          onSegmentFilter={handleSegmentFilter}
          onGoToManagers={onGoToManagers}
          selectedStatusKey={selectedStatusKey}
        />
      )}
    </section>
  );
}

export default UsageReport;
